import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bed, Money, TrendUp, SignIn, SignOut, ArrowRight, CreditCard } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { StatusBadge } from "@/components/StatusBadge";
import { VacantProperties } from "@/components/VacantProperties";

function SubscriptionCard({ settings, fmt }) {
  const sub = settings?.subscription;
  const [pricing, setPricing] = useState(null);
  const [renewing, setRenewing] = useState(false);
  useEffect(() => {
    api.get("/subscription/pricing").then(({ data }) => setPricing(data)).catch(() => setPricing({ available: false }));
  }, []);
  if (!sub) return null;
  const renew = async () => {
    setRenewing(true);
    try {
      const { data } = await api.post("/subscription/renew", { plan: sub.plan || "monthly", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setRenewing(false);
    }
  };
  const amount = pricing?.available ? pricing[sub.plan || "monthly"] : null;
  return (
    <div className="border border-border bg-card p-5" data-testid="dashboard-subscription-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-primary/10">
            <CreditCard size={20} className="text-primary" />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Subscription</div>
            <div className="mt-0.5 flex items-center gap-2">
              <span className="font-heading text-lg font-bold capitalize" data-testid="subscription-plan">{sub.plan || "monthly"}</span>
              <span data-testid="subscription-status"
                className={`border px-2 py-0.5 text-[11px] font-medium ${sub.status === "active" ? "border-status-confirmed/40 bg-status-confirmed/10 text-status-confirmed" : sub.status === "trialing" ? "border-status-pending/40 bg-status-pending/10 text-status-pending" : "border-destructive/40 bg-destructive/10 text-destructive"}`}>
                {sub.status === "trialing" ? "Free trial" : sub.status}
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <div>
            <div className="text-[11px] text-muted-foreground">{sub.status === "trialing" ? "Free trial ends" : "Next billing date"}</div>
            <div className="text-sm font-semibold" data-testid="subscription-next-billing">
              {sub.next_billing_date ? format(new Date(sub.next_billing_date), "dd MMM yyyy") : "—"}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground">{sub.plan === "yearly" ? "Per year" : "Per month"}</div>
            <div className="text-sm font-semibold" data-testid="subscription-amount">
              {amount != null ? fmt(amount) : "Contact us for pricing"}
            </div>
          </div>
          <button onClick={renew} disabled={renewing || !pricing?.available} data-testid="subscription-renew-button"
            className="rounded-sm bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50">
            {renewing ? "Redirecting…" : pricing?.available ? "Renew subscription" : "Contact us for pricing"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const { fmt, settings } = useCurrency();

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (!stats)
    return (
      <div className="flex h-64 items-center justify-center" data-testid="dashboard-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );

  const cards = [
    { label: "Occupancy today", value: `${stats.occupancy_rate}%`, sub: `${stats.occupied_today} of ${stats.total_rooms} rooms`, icon: Bed, testId: "dashboard-occupancy-card" },
    { label: "Revenue this month", value: fmt(stats.revenue_mtd), sub: "All recorded payments", icon: Money, testId: "dashboard-revenue-card" },
    { label: "ADR (month)", value: fmt(stats.adr), sub: "Average daily rate", icon: TrendUp, testId: "dashboard-adr-card" },
    { label: "Arrivals / Departures", value: `${stats.arrivals.length} / ${stats.departures.length}`, sub: "Today", icon: SignIn, testId: "dashboard-arrivals-card" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Front Desk Overview</h1>
          <p className="text-sm text-muted-foreground">{format(new Date(), "EEEE, dd MMMM yyyy")}</p>
        </div>
        <Link to="/bookings" data-testid="dashboard-new-booking-link"
          className="flex items-center gap-2 rounded-sm bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90">
          Manage bookings <ArrowRight size={16} />
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map(({ label, value, sub, icon: Icon, testId }) => (
          <div key={label} data-testid={testId}
            className="border border-border bg-card p-5 transition-shadow hover:shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
              <Icon size={20} weight="duotone" className="text-primary" />
            </div>
            <div className="font-heading mt-3 text-3xl font-bold" data-testid={`${testId}-value`}>{value}</div>
            <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
          </div>
        ))}
      </div>

      <SubscriptionCard settings={settings} fmt={fmt} />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
        <div className="border border-border bg-card p-5 xl:col-span-2" data-testid="dashboard-occupancy-chart">
          <h2 className="font-heading text-lg font-bold">Occupancy — next 14 days</h2>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.occupancy_series}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => format(new Date(`${d}T00:00:00`), "dd MMM")}
                  tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                <Tooltip formatter={(v) => [`${v} rooms`, "Occupied"]} labelFormatter={(d) => format(new Date(`${d}T00:00:00`), "dd MMM yyyy")} />
                <Bar dataKey="occupied" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-border bg-card p-5" data-testid="dashboard-room-status">
          <h2 className="font-heading text-lg font-bold">Room status</h2>
          <div className="mt-4 space-y-3">
            {Object.entries(stats.room_status_counts).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between border-b border-border pb-2 last:border-0">
                <StatusBadge status={status} testId={`room-status-${status}`} />
                <span className="font-heading text-xl font-bold" data-testid={`room-status-count-${status}`}>{count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-border bg-card p-5" data-testid="dashboard-today-lists">
          <h2 className="font-heading text-lg font-bold">Today's movement</h2>
          <div className="mt-4 space-y-4">
            <div>
              <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-status-confirmed">
                <SignIn size={14} /> Arrivals
              </div>
              {stats.arrivals.length === 0 && <div className="text-xs text-muted-foreground">No arrivals today</div>}
              {stats.arrivals.map((b) => (
                <div key={b.id} className="flex justify-between py-1 text-sm" data-testid={`arrival-${b.id}`}>
                  <span>{b.guest_name}</span>
                  <span className="text-muted-foreground">Room {b.room_name}</span>
                </div>
              ))}
            </div>
            <div>
              <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-status-checked-in">
                <SignOut size={14} /> Departures
              </div>
              {stats.departures.length === 0 && <div className="text-xs text-muted-foreground">No departures today</div>}
              {stats.departures.map((b) => (
                <div key={b.id} className="flex justify-between py-1 text-sm" data-testid={`departure-${b.id}`}>
                  <span>{b.guest_name}</span>
                  <span className="text-muted-foreground">Room {b.room_name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <VacantProperties />

      <div className="border border-border bg-card" data-testid="dashboard-recent-bookings">
        <div className="flex items-center justify-between border-b border-border p-5">
          <h2 className="font-heading text-lg font-bold">Recent bookings</h2>
          <Link to="/bookings" className="text-sm text-primary hover:underline" data-testid="view-all-bookings-link">View all</Link>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="p-3 font-medium">Guest</th>
              <th className="p-3 font-medium">Room</th>
              <th className="p-3 font-medium">Dates</th>
              <th className="p-3 text-right font-medium">Total</th>
              <th className="p-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {stats.recent_bookings.map((b) => (
              <tr key={b.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`recent-booking-${b.id}`}>
                <td className="p-3 font-medium">{b.guest_name}</td>
                <td className="p-3">{b.room_name} · {b.room_type}</td>
                <td className="p-3 text-muted-foreground">{b.check_in} → {b.check_out}</td>
                <td className="p-3 text-right font-medium">{fmt(b.total)}</td>
                <td className="p-3"><StatusBadge status={b.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
